/* oxlint-disable no-useless-catch */

import { handleRequestFileUpload } from '@/shared/api';
import axios from 'axios';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { generateToken } from '../auth';
import { showMessageError } from '../message';

/**
 * Downloads a binary ECG resource and exposes it as 16-bit samples. Fetches the URL with axios as an arraybuffer using a freshly generated access token in the 'access-token' header and wraps the buffer in an Int16Array; a missing URL or a request failure logs the error, surfaces it through showMessageError and yields undefined.
 *
 * @param url - Signed URL of the binary resource to download.
 * @returns Promise resolving to the samples as an Int16Array, or undefined when no URL was given or the request failed.
 */
const downloadBinaryResource = async (url?: any) => {
  if (url) {
    try {
      const accessToken = await generateToken();
      const response = await axios.get(url, {
        responseType: 'arraybuffer',
        headers: {
          'access-token': accessToken,
        },
      });
      const buffer = response.data;
      const data = new Int16Array(buffer);
      return data;
    } catch (error) {
      console.error('Failed to download binary resource: ', error);
      showMessageError({ content: error instanceof Error ? error.message : String(error) });
      return undefined;
    }
  }
  return undefined;
};

/**
 * Turns a base64 data URI into its raw byte array so file payloads can be uploaded or downloaded. Strips everything up to the ';base64,' marker, decodes the remainder with atob, and maps each character to its code point through Uint8Array.from.
 *
 * @param dataURI - Data URI containing a base64-encoded payload.
 * @returns Byte array decoded from the data URI payload.
 */
const dataURIToUInt8Array = (dataURI: any) => {
  const BASE64_MARKER = ';base64,';
  const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
  const base64String = dataURI.substring(base64Index);
  /**
   * Holds the decoded byte array produced from a base64 string. Built by decoding the base64 payload with atob and mapping each character to its code point via Uint8Array.from.
   *
   * @returns The decoded byte array.
   */
  const uint8Array = Uint8Array.from(atob(base64String), (c) => c.charCodeAt(0));
  return uint8Array;
};

/**
 * Uploads one binary payload to a presigned storage URL with an HTTP PUT. For signature payloads it strips the 'data:image/png;base64,' prefix, re-encodes the remainder into a Buffer and adds an image/png content-type header; otherwise the binary data is sent unchanged.
 *
 * @param url - Presigned URL to PUT the payload to.
 * @param binaryData - Payload to upload, a base64 PNG data URI when isSignature is set.
 * @param isSignature - Flag selecting PNG signature handling over raw binary upload.
 * @returns Promise resolving to the fetch response of the PUT request.
 */
const putFileToUrl = (url: any, binaryData: any, isSignature: any) => {
  const uploadData = isSignature
    ? Buffer.from(_.replace(binaryData, 'data:image/png;base64,', ''), 'base64')
    : binaryData;
  const options: any = {
    method: 'PUT',
    body: uploadData,
  };
  if (isSignature) {
    _.assign(options, {
      headers: { 'Content-Type': 'image/png; charset=utf-8' },
    });
  }
  return fetch(url, options);
};

/**
 * Requests presigned upload URLs for a file type and pushes every chunk of binary data to them. Calls handleRequestFileUpload with the type and payload length, PUTs each payload to its matching URL in parallel through Promise.all, and returns the single URL when only one was issued, the URL list when several were, or null when none came back; request errors are rethrown.
 *
 * @param binaryData - Array of binary payloads to upload, one per presigned URL.
 * @param type - File type passed to the upload-URL request.
 * @param isSignature - Flag forwarded to each PUT so PNG signature payloads are re-encoded.
 * @returns Promise resolving to the upload URL, the array of URLs, or null when no URL was issued.
 */
const uploadFileToUrl = async (binaryData: any, type: any, isSignature = false) => {
  try {
    const uploadFileUrls = await handleRequestFileUpload(type, binaryData.length);
    if (uploadFileUrls?.length) {
      /**
       * Holds the array of pending PUT-upload promises, one per presigned URL. Built by mapping each upload URL through putFileToUrl with its matching payload and the signature flag.
       *
       * @returns The in-flight upload promises.
       */
      const putFileToUrlPromises = _.map(uploadFileUrls, (uploadFileUrl, index) =>
        putFileToUrl(uploadFileUrl, binaryData[index], isSignature),
      );
      await Promise.all(putFileToUrlPromises);
      return uploadFileUrls.length === 1 ? uploadFileUrls[0] : uploadFileUrls;
    }
    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Downloads a file behind an authenticated URL and hands it to the browser as a save prompt. Fetches the URL with axios as an arraybuffer using a generated access token, passes the payload to fileDownload under the given filename, and on failure logs the error message and shows it through showMessageError.
 *
 * @param url - Signed URL of the file to download.
 * @param filename - Name to save the downloaded file under.
 * @returns Promise resolving once the download has been triggered or the error reported.
 */
const handleDownloadFile = async (url: any, filename: any) => {
  try {
    const accessToken = await generateToken();
    const response = await axios.get(url, {
      responseType: 'arraybuffer',
      headers: {
        'access-token': accessToken,
      },
    });
    fileDownload(response.data, filename);
  } catch (error) {
    console.error('Failed to download file: ', error instanceof Error ? error.message : error);
    showMessageError({ content: error instanceof Error ? error.message : String(error) });
  }
};

export { dataURIToUInt8Array, downloadBinaryResource, handleDownloadFile, uploadFileToUrl };
